import {
  Button,
  Dialog,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import { fontSize, palette } from '../..';
import { IArrow, IColumn, IConnection, ISelectedTable, ITable } from '../../redux/_Dataset/types';
import { useEffect, useState } from 'react';
import { TAlert } from '../../types/types';
import { getQueryStructure } from './utils';
import { NotificationDialog, PopUpSpinner } from '../CommonFunctions/DialogComponents';
import { getColumnTypeswithCustomQuery, getSampleRecordsForCustomQuery } from '../../api';
import { isNameAllowed } from '../CommonFunctions/CommonFunctions';
import { useDispatch } from 'react-redux';
import ShortUniqueId from 'short-unique-id';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux';
import { AddArrow, RemoveArrow, SetSelectedTables } from '../../redux/_Dataset/dataset.action';
import ConfirmDialog from './ConfirmDialog';
interface ICustomQueryEditor {
  isNew: boolean;
  tableInfo?: ISelectedTable | null;
  connection: IConnection;
  onEditorClose: () => void;
}

const buildMissingColumnsMessage = (cols: string[]) => {
  const unique = [...new Set(cols)];

  const colWord = unique.length > 1 ? "columns" : "column";
  const isAre = unique.length > 1 ? "are" : "is";

  return `Please note that ${colWord} ${unique.join(", ")} ${isAre} missing now which ${isAre} used in table joins.`;
};

function CustomQueryEditor({ isNew, tableInfo, connection, onEditorClose }: ICustomQueryEditor) {
  const [query, setQuery] = useState(tableInfo?.customQuery ?? '');
  const { selectedTables, arrows } = useSelector((state: RootState) => ({
    selectedTables: state.dataset.selectedTables,
    arrows: state.dataset.arrows,
  }));
  const dispatch = useDispatch();
  const [tableName, setTableName] = useState(tableInfo?.table ?? '');
  const [results, setResults] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<TAlert | null>(null);
  const [columns, setColumns] = useState<string[]>([]);
  const uid = new ShortUniqueId({ length: 4 });
  const columnId = new ShortUniqueId({ length: 4, dictionary: 'alpha' });
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingUpdatedTable, setPendingUpdatedTable] = useState<ISelectedTable | null>(null);
  const [arrowsToRemove, setArrowsToRemove] = useState<string[]>([]);
  const [arrowsToUpdate, setArrowsToUpdate] = useState<IArrow[]>([]);
  const [missingColsName, setMissingColsName] = useState<string[]>([]);

  useEffect(() => {
    if (isNew) {
      setQuery(getQueryStructure(connection.vendor!));
      setTableName('');
    } else {
      const query = tableInfo?.customQuery ?? getQueryStructure(connection.vendor!);
      setQuery(query);
      setTableName(tableInfo?.table ?? '');
    }
  }, []);
  const handleCustomPreview = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    const res = await getSampleRecordsForCustomQuery({
      connectionValue: connection.id,
      num: 250,
      workspaceId: '',
      data: { query },
    });
    if (res.isError) {
      setAlert({
        severity: 'error',
        message:
          res.statusCode && res.statusCode === 422
            ? 'The entered columns or tables do not exist. Please check your query and try again.'
            : (res.message ?? 'Cannot execute the query. Please enter a valid query.'),
      });
      setLoading(false);
      return;
    }
    if (res.data) {
      setResults(res.data);
      const row = res.data[0];
      if (!row) setColumns([]);
      else setColumns(Object.keys(row));
    }
    setLoading(false);
  };

  const applyUpdateAndRemove = (updatedTable: ISelectedTable, updatedArrows: IArrow[]) => {
    dispatch(RemoveArrow(arrowsToRemove));
    updatedArrows.forEach(a => dispatch(AddArrow(a)));

    dispatch(
      SetSelectedTables(
        selectedTables.map(t => (t.id === updatedTable.id ? updatedTable : t))
      )
    );

    onEditorClose();
  };

  // this will return updated arrows if the culumn is exist else it will addArrowId to remove
  const checkConnectionImpact = (arrows: IArrow[], updatedTable: ISelectedTable) => {
    const updatedArrows: IArrow[] = [];
    let invalidArrows: number = 0;
    const missingCols: string[] = [];

    if (!tableInfo) return {
      updatedArrows,
      invalidArrows,
      missingCols,
      arrowsToRemove: [],
    }

    const connectedArrows = arrows.filter(
      arrow => arrow.startId === tableInfo.id || arrow.endId === tableInfo.id
    );

    if (connectedArrows.length === 0) return {
      updatedArrows,
      invalidArrows,
      missingCols,
      arrowsToRemove: [],
    }

    connectedArrows.forEach(oldArrow => {
      let isValid = true;
      const newArrow = { ...oldArrow };

      if (oldArrow.startId === tableInfo.id) {
        const oldCol = tableInfo.columns.find(c => c.uid === oldArrow.start);
        const match = oldCol && updatedTable.columns.find(
          col =>
            col.columnName.toLowerCase() === oldCol.columnName.toLowerCase() &&
            col.dataType.toLowerCase() === oldCol.dataType.toLowerCase()
        );
        if (match) newArrow.start = match.uid;
        else {
          isValid = false;
          if (oldCol) missingCols.push(oldCol.columnName);
        }
      }

      if (oldArrow.endId === tableInfo.id) {
        const oldCol = tableInfo.columns.find(c => c.uid === oldArrow.end);
        const match = oldCol && updatedTable.columns.find(
          col =>
            col.columnName === oldCol.columnName &&
            col.dataType.toLowerCase() === oldCol.dataType.toLowerCase()
        );
        if (match) newArrow.end = match.uid;
        else {
          isValid = false;
          if (oldCol) missingCols.push(oldCol.columnName);
        }
      }

      if (isValid) updatedArrows.push(newArrow);
      else {
        invalidArrows++;
      }
    });

    const arrowsToRemove = connectedArrows.map(a => a.arrowId);

    return {
      updatedArrows,
      invalidArrows,
      missingCols,
      arrowsToRemove,
    };
  };

  const handleCreateCustomQueryTable = async () => {
    if (tableName.includes(' ') || !isNameAllowed(tableName)) {
      setAlert({
        severity: 'error',
        message: `"${tableName}" is a invalid table name`,
      });
      return;
    }
    setLoading(true);
    const res = await getColumnTypeswithCustomQuery(connection.id, { customQuery: query }, '', []);
    setLoading(false);
    if (res.isError) {
      setAlert({
        severity: 'error',
        message: 'Unable to create a table',
      });
      return;
    }
    if (isNew) {
      const newTable: ISelectedTable = {
        id: uid(),
        vendor: connection.vendor!,
        table: tableName,
        connectionId: connection.id,
        connectionName: connection.label,
        isFlatFile: false,
        flatFileId: '',
        isCustomQuery: true,
        customQuery: query,
        // isView: false,
        database: 'custom query',
        schema: 'custom query',
        isNewTable: true,
        tablePositionX: 0,
        tablePositionY: 0,
        columns: res.data.map((column: any) => ({
          ...column,
          uid: columnId(),
        })),
        alias: tableName,
        isSelected: true,
      };
      dispatch(SetSelectedTables([...selectedTables, newTable]));
    } else {
      if (!tableInfo || !res.data) return
      const isQueryModified = tableInfo?.customQuery !== query
      const isTableNameChange = tableInfo.table !== tableName

      if (isQueryModified) {
        const updatedTable: ISelectedTable = {
          ...tableInfo,
          table: tableName,
          customQuery: query,
          isNewTable: true,
          tablePositionX: tableInfo?.tablePositionX ?? 0,
          tablePositionY: tableInfo?.tablePositionY ?? 0,
          columns: res.data.map((column: any) => ({
            ...column,
            uid: columnId(),
          })),
          alias: tableName,
          isSelected: true,
        };

        const {
          updatedArrows,
          invalidArrows,
          missingCols,
          arrowsToRemove,
        } = checkConnectionImpact(arrows, updatedTable)

        if (invalidArrows > 0) {
          setArrowsToRemove(arrowsToRemove);
          setMissingColsName([...new Set(missingCols)]);
          setPendingUpdatedTable(updatedTable);
          setArrowsToUpdate(updatedArrows)
          setShowConfirm(true);
          return;
        }

        applyUpdateAndRemove(updatedTable, updatedArrows);
      }
      if (isTableNameChange && !isQueryModified) {
        dispatch(
          SetSelectedTables(
            selectedTables.map(t => (t.id === tableInfo.id ? { ...tableInfo, alias: tableName, table: tableName } : t))
          )
        );
      }
    };
    onEditorClose();
  };

  return (
    <Dialog
      open={true}
      maxWidth="lg"
      fullWidth={true}
      PaperProps={{
        sx: {
          minHeight: '90%',
        },
      }}
    >
      {!results ? (
        <div className="CustomQuerybox">
          <form onSubmit={handleCustomPreview}>
            <textarea
              spellCheck="false"
              className="customTextArea"
              name="customQuery"
              id="customQuery"
              style={{ color: palette.primary.contrastText }}
              // ref={textareaRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <div
              style={{
                display: 'flex',
                justifyContent: 'end',
                marginTop: '10px',
                gap: '1%',
              }}
            >
              <Button
                variant="contained"
                onClick={onEditorClose}
                id="cancelButton"
                sx={{
                  textTransform: 'none',
                  lineHeight: 'normal',
                  fontSize: fontSize.medium,
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                id="setButton"
                sx={{
                  textTransform: 'none',
                }}
                style={{
                  backgroundColor: '#2bb9bb',
                  lineHeight: 'normal',
                  fontSize: fontSize.medium,
                }}
                type="submit"
              >
                Preview
              </Button>
            </div>
          </form>
        </div>
      ) : columns.length > 0 ? (
        <>
          <DialogContent>
            <Table stickyHeader padding="normal">
              <TableHead>
                <TableRow>
                  {columns.map((column, i: number) => {
                    return (
                      <TableCell
                        style={{
                          fontWeight: 'bold',

                          backgroundColor: '#e8eaf6',
                        }}
                        key={i}
                      >
                        {column}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody style={{ width: 'auto', paddingTop: '200px' }}>
                {results.map((data: any, i: number) => {
                  return (
                    <TableRow key={i} id="TRow">
                      {columns.map((column: string) => {
                        return (
                          <TableCell key={column} id="TColumn">
                            {data[column]}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </DialogContent>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '2%',
            }}
          >
            <div>
              <Button
                variant="contained"
                onClick={() => {
                  setResults(null);
                  setColumns([]);
                }}
                id="cancelButton"
                sx={{ textTransform: 'none' }}
              >
                Cancel
              </Button>
            </div>
            <div>
              <Tooltip
                title="Click to Edit"
                sx={{
                  '& .MuiTooltip-tooltip': {
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  },
                  fontSize: '12px',
                }}
              >
                <TextField
                  sx={{
                    flex: 1,
                    margin: 'auto 20px',
                    maxWidth: '200px',
                    '& label': { fontSize: '14px', color: '#af99db' },
                    '& .MuiOutlinedInput-root': { borderRadius: 1 },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#af99db',
                    },
                    '& .css-ddqwwv-MuiFormLabel-root-MuiInputLabel-root.Mui-focused': {
                      color: '#af99db',
                    },
                    '& .css-ejwmqw-MuiInputBase-root-MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                    {
                      borderColor: '#af99db',
                    },
                  }}
                  InputProps={{
                    style: {
                      fontSize: '14px',
                    },
                  }}
                  onChange={(e) => {
                    setTableName(e.target.value);
                  }}
                  id="outlined-size-small"
                  size="small"
                  // color="primary"
                  error={tableName !== '' && (tableName.includes(' ') || !isNameAllowed(tableName))}
                  value={tableName}
                  label="Name Custom Query"
                />
              </Tooltip>

              <Button
                variant="contained"
                onClick={handleCreateCustomQueryTable}
                id="setButton"
                sx={{ textTransform: 'none' }}
                style={{
                  backgroundColor: 'white',
                  color: '#2bb9bb',
                  outlineColor: '#2bb9bb',
                  outline: '1px solid #2bb9bb',
                  border: 'none',
                }}
              >
                OK
              </Button>
            </div>
          </div>
        </>
      ) : (
        <div>No data to display</div>
      )}

      <PopUpSpinner
        show={loading}
        sx={{ background: 'transparent' }}
        paperProps={{
          sx: {
            backgroundColor: 'transparent',
            color: 'white',
            boxShadow: 'none',
          },
        }}
      />
      {alert && (
        <NotificationDialog
          onCloseAlert={() => setAlert(null)}
          severity={alert.severity}
          testMessage={alert.message}
          openAlert={!!alert}
        />
      )}

      <ConfirmDialog
        open={showConfirm}
        title="Update Query?"
        message={`${buildMissingColumnsMessage(missingColsName)} 
Do you want to Update?`}
        cancelText="Cancel"
        onCancel={() => setShowConfirm(false)}
        onConfirm={() => {
          setShowConfirm(false);
          if (pendingUpdatedTable) {
            applyUpdateAndRemove(pendingUpdatedTable, arrowsToUpdate);
          }
        }}
        confirmText='Update'
      />

    </Dialog>
  );
}
export default CustomQueryEditor;
